import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api.ts';

interface Data {
  siret: { extraId: string; name: string; email: string; siret: string; status: 'CEASED' | 'NOT_FOUND' | 'NAME_MISMATCH'; checkedAt: string | null; legalName: string | null }[];
  vigilance: { id: string; extraId: string; name: string; email: string; fileName: string; uploadedAt: string }[];
  references: { extraId: string; name: string; email: string; confirmed: number; pending: number; declined: number }[];
}

interface Detail {
  id: string;
  kind: 'EMPLOYER' | 'CLIENT';
  name: string;
  companyAddress: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  channel: 'EMAIL' | 'SMS' | 'BOTH';
  roleTitle: string | null;
  periodLabel: string | null;
  status: string;
  respondedAt: string | null;
  workedWith: boolean | null;
  rating: number | null;
  score: number | null;
  criteria: { code: string; label: string; score: number }[];
  wouldRehire: boolean | null;
  comment: string | null;
}

interface Criterion {
  id: string;
  code: string;
  label: string;
  hint: string | null;
  groupLabel: string | null;
  weight: number;
  active: boolean;
}

// Une ligne modifiable ; l'enregistrement se fait au clic pour éviter une requête à chaque frappe.
function CriterionRow({ c, onSave }: { c: Criterion; onSave: (patch: { label: string; weight: number; active: boolean }) => Promise<void> }) {
  const [label, setLabel] = useState(c.label);
  const [weight, setWeight] = useState(String(c.weight));
  const [active, setActive] = useState(c.active);
  const dirty = label !== c.label || Number(weight) !== c.weight || active !== c.active;
  return (
    <tr>
      <td><input className="input" aria-label={`Libellé ${c.code}`} value={label} maxLength={120} onChange={(e) => setLabel(e.target.value)} /></td>
      <td style={{ width: 90 }}><input className="input" type="number" min={0} max={10} step={1} aria-label={`Poids ${c.code}`} value={weight} onChange={(e) => setWeight(e.target.value)} /></td>
      <td style={{ width: 70, textAlign: 'center' }}><input type="checkbox" aria-label={`Actif ${c.code}`} checked={active} onChange={(e) => setActive(e.target.checked)} /></td>
      <td style={{ width: 110, textAlign: 'right' }}>
        <button className="btn btn-secondary btn-sm" disabled={!dirty || !label.trim()} onClick={() => onSave({ label: label.trim(), weight: Number(weight), active })}>Enregistrer</button>
      </td>
    </tr>
  );
}

const SIRET_LABELS = {
  CEASED: { label: 'Cessé', badge: 'badge-unavailable' },
  NOT_FOUND: { label: 'Introuvable ou non diffusible', badge: 'badge-pending' },
  NAME_MISMATCH: { label: 'Nom différent', badge: 'badge-pending' },
} as const;

const day = (iso: string) => new Date(iso).toLocaleDateString('fr-FR');

export function AdminVerifications() {
  const [data, setData] = useState<Data | null>(null);
  const [detail, setDetail] = useState<{ name: string; items: Detail[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [criteria, setCriteria] = useState<Criterion[] | null>(null);

  const load = useCallback(() => {
    api.get<Data>('/admin/verifications').then(setData);
    api.get<{ criteria: Criterion[] }>('/admin/verifications/reference-criteria').then((r) => setCriteria(r.criteria));
  }, []);
  useEffect(load, [load]);

  async function act(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Action impossible.');
    }
  }

  function reject(id: string) {
    const reason = window.prompt('Motif du refus (visible par l’extra) :');
    if (reason) act(() => api.post(`/admin/verifications/vigilance/${id}/reject`, { reason }));
  }

  async function showReferences(extraId: string, name: string) {
    const res = await api.get<{ references: Detail[] }>(`/admin/verifications/extras/${extraId}/references`);
    setDetail({ name, items: res.references });
  }

  if (!data) return <div className="empty-state">Chargement…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {error && <div className="error-banner">{error}</div>}

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Attestations de vigilance à contrôler ({data.vigilance.length})</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
          Ouvrez le PDF, saisissez son code de sécurité sur le site de vérification de l'URSSAF, puis cliquez sur « Code vérifié ». La validité est de 6 mois.
        </div>
        {data.vigilance.length === 0 && <div className="empty-state">Aucune attestation en attente.</div>}
        {data.vigilance.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Extra</th><th>Fichier</th><th>Déposée le</th><th /></tr></thead>
              <tbody>
                {data.vigilance.map((v) => (
                  <tr key={v.id}>
                    <td><div style={{ fontWeight: 700 }}>{v.name}</div><div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{v.email}</div></td>
                    <td><a href={`/api/admin/verifications/vigilance/${v.id}/file`} target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: 'var(--accent-dark)' }}>{v.fileName}</a></td>
                    <td>{day(v.uploadedAt)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-primary btn-sm" onClick={() => act(() => api.post(`/admin/verifications/vigilance/${v.id}/verify`, { securityCodeChecked: true }))}>Code vérifié</button>{' '}
                      <button className="btn btn-danger-outline btn-sm" onClick={() => reject(v.id)}>Refuser</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>SIRET à examiner ({data.siret.length})</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
          Résultat du contrôle automatique dans la base officielle. Une entreprise non diffusible apparaît comme « introuvable » : vous pouvez la valider à la main après vérification.
        </div>
        {data.siret.length === 0 && <div className="empty-state">Aucun SIRET à examiner.</div>}
        {data.siret.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Extra</th><th>SIRET</th><th>Résultat</th><th /></tr></thead>
              <tbody>
                {data.siret.map((s) => (
                  <tr key={s.extraId}>
                    <td><div style={{ fontWeight: 700 }}>{s.name}</div><div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.email}</div></td>
                    <td>{s.siret}{s.legalName && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{s.legalName}</div>}</td>
                    <td>
                      <span className={`badge ${SIRET_LABELS[s.status].badge}`}>{SIRET_LABELS[s.status].label}</span>
                      {s.checkedAt && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>le {day(s.checkedAt)}</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => window.confirm(`Valider manuellement le SIRET de ${s.name} ?`) && act(() => api.post(`/admin/verifications/siret/${s.extraId}/validate`))}>Valider à la main</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Références ({data.references.length} extras concernés)</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10 }}>Les notes et commentaires des référents ne sont visibles que d'ici.</div>
        {data.references.length === 0 && <div className="empty-state">Aucune référence demandée.</div>}
        {data.references.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Extra</th><th>Confirmées</th><th>En attente</th><th>Sans suite</th><th /></tr></thead>
              <tbody>
                {data.references.map((r) => (
                  <tr key={r.extraId}>
                    <td><div style={{ fontWeight: 700 }}>{r.name}</div><div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.email}</div></td>
                    <td>{r.confirmed}</td><td>{r.pending}</td><td>{r.declined}</td>
                    <td style={{ textAlign: 'right' }}><button className="btn btn-secondary btn-sm" onClick={() => showReferences(r.extraId, r.name)}>Voir le détail</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {detail && (
          <div className="card" style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 800 }}>Références de {detail.name}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDetail(null)}>Fermer</button>
            </div>
            {detail.items.map((d) => (
              <div key={d.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 10, fontSize: 13, lineHeight: 1.6 }}>
                <strong>{d.name}</strong>{d.companyAddress && ` (${d.companyAddress})`} · {d.kind === 'CLIENT' ? 'client' : 'employeur'} · {d.contactName ?? 'contact non précisé'} ({[d.email, d.phone].filter(Boolean).join(' / ')})
                {(d.roleTitle || d.periodLabel) && <> · {[d.roleTitle, d.periodLabel].filter(Boolean).join(', ')}</>}
                <br />
                {d.status === 'CONFIRMED'
                  ? <>A travaillé avec lui : oui · note globale {d.score?.toFixed(1) ?? d.rating}/5{d.criteria.length > 0 && <> ({d.criteria.map((c) => `${c.label} ${c.score}`).join(', ')})</>}{d.wouldRehire !== null && ` · réembaucherait : ${d.wouldRehire ? 'oui' : 'non'}`}{d.comment && <><br />« {d.comment} »</>}</>
                  : <>Statut : {d.status === 'REQUESTED' ? 'en attente' : d.status === 'EXPIRED' ? 'expirée' : d.workedWith === false ? 'n’a pas travaillé avec lui' : 'sans suite'}</>}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 6 }}>Critères de notation des références</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.6 }}>
          Le référent note chaque critère de 1 à 5 : ceux communs à tous les postes, plus ceux de la famille du poste occupé. La note globale est la moyenne pondérée par ces poids
          (0 : le critère est demandé mais ne compte pas). Désactiver un critère le retire des nouveaux questionnaires ; les réponses déjà reçues sont conservées.
        </div>
        {criteria && [...new Set(criteria.map((c) => c.groupLabel ?? ''))].map((group) => (
          <div key={group || 'common'} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{group || 'Commun à tous les postes'}</div>
            <div className="table-wrap">
              <table className="data-table">
                <thead><tr><th>Libellé</th><th>Poids</th><th>Actif</th><th /></tr></thead>
                <tbody>
                  {criteria.filter((c) => (c.groupLabel ?? '') === group).map((c) => (
                    <CriterionRow key={`${c.id}:${c.label}:${c.weight}:${c.active}`} c={c} onSave={(patch) => act(() => api.patch(`/admin/verifications/reference-criteria/${c.id}`, patch))} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
